import { useCallback, type FocusEvent, type MouseEvent } from 'react';
import { Icon } from '@aqarmap/aqarmap-ui';
import { Trans, useTranslation } from 'react-i18next';
import ErrorMessage from '~/components/ErrorMessage';
import ListLoader from '~/components/ListLoader';
import { SelectedPaymentType, UserCardType } from '~/types/paymentTypes';
import UserCardForm from '~/components/UserCardForm';
import { formatNumber } from '~/lib';
import PayWithCashOption from '~/components/PurchasePaymentMethod/PayWithCashOption';
import PaymentOption from '~/components/PurchasePaymentMethod/PaymentOption';
import {
  UserCardFormHandle,
  UserCardFormProps,
} from '~/components/UserCardForm/types';
import { useGetUserCardsQuery } from '~/redux/api/userApiSlice';
import { useWallet } from '~/hooks';

type Props = {
  setSelectedPayment: (value: SelectedPaymentType) => void;
  selectedPayment?: SelectedPaymentType;
  error?: string;
  enableWallet?: boolean;
  enableCash?: boolean;
  newCardProps: UserCardFormProps & {
    ref: React.RefObject<UserCardFormHandle>;
  };
  title: string;
};

export default function PurchasePaymentMethod({
  setSelectedPayment,
  selectedPayment,
  error,
  enableWallet = false,
  enableCash = false,
  newCardProps,
  title,
}: Props) {
  const { t } = useTranslation('app');
  const { walletBalance, isWalletLoading, isWalletSuccess, userHasWallet } =
    useWallet({});
  const {
    data: cardsData,
    isLoading: isCardsLoading,
    isSuccess: isCardsSuccess,
  } = useGetUserCardsQuery(undefined);
  const savedCards = cardsData?.data ?? [];
  const isPaymentLoading = isCardsLoading || isWalletLoading;
  const isPaymentSuccess = isCardsSuccess && isWalletSuccess;

  const getCardTitle = (cardBrand: string) =>
    cardBrand === '' ? t('addPaymentCard') : t('card');

  const selectWallet = useCallback(() => {
    if (selectedPayment?.payWithWallet) return;
    setSelectedPayment({
      id: 0,
      isNew: false,
      payWithWallet: true,
      payWithCash: false,
    });
  }, [selectedPayment, setSelectedPayment]);

  const selectSavedCard = useCallback(
    (cardId: number) => {
      if (selectedPayment?.id === cardId && !selectedPayment?.isNew) return;
      setSelectedPayment({
        id: cardId,
        isNew: false,
        payWithWallet: false,
        payWithCash: false,
      });
    },
    [selectedPayment, setSelectedPayment],
  );

  const selectNewCard = useCallback(() => {
    if (selectedPayment?.isNew) return;
    setSelectedPayment({
      id: 0,
      isNew: true,
      payWithWallet: false,
      payWithCash: false,
    });
  }, [selectedPayment, setSelectedPayment]);

  const stopPropagation = useCallback(
    (event: MouseEvent<HTMLDivElement> | FocusEvent<HTMLDivElement>) => {
      event.stopPropagation();
    },
    [],
  );
  const onCashSelect = useCallback(() => {
    if (selectedPayment?.payWithCash) return;
    setSelectedPayment({
      id: 0,
      isNew: false,
      payWithWallet: false,
      payWithCash: true,
    });
  }, [selectedPayment, setSelectedPayment]);

  return (
    <div className="flex flex-col gap-3x w-full md:w-[55%] mb-[92px] md:mb-0">
      <p className="text-title_3">{title}</p>
      {error && <ErrorMessage message={error} />}

      {isPaymentLoading || !isPaymentSuccess ? (
        <ListLoader height={48} count={4} />
      ) : (
        <div className="flex flex-col gap-2x">
          {enableWallet && userHasWallet && (
            <PaymentOption
              className="border border-gray rounded-[8px] p-3x"
              checked={!!selectedPayment?.payWithWallet}
              onClick={selectWallet}
              radioName="selectedPayment"
              title={
                <div className="flex gap-0.5x md:gap-x flex-col md:flex-row">
                  <p className="text-link md:text-title_4">
                    {t('useWalletBalance')}
                  </p>
                  <p className="text-caption-1 text-black">
                    (
                    <span className="text-title-5  text-green_dark">
                      <Trans
                        i18nKey="priceValue"
                        t={t}
                        values={{
                          value: formatNumber(walletBalance),
                        }}
                        components={{
                          tag: (
                            <span className="text-caption-1 text-black">
                              placeholder
                            </span>
                          ),
                        }}
                      />
                    </span>
                    )
                  </p>
                </div>
              }
              rightContent={
                <Icon name="wallet" size="24px" className="text-gray__dark_2" />
              }
            />
          )}

          {enableCash && (
            <PayWithCashOption
              checked={!!selectedPayment?.payWithCash}
              onSelect={onCashSelect}
            />
          )}

          {savedCards.map((card: UserCardType) => {
            const isSelected =
              selectedPayment?.id === card.id && !selectedPayment?.isNew;

            return (
              <PaymentOption
                className="border border-gray rounded-[8px] p-3x "
                key={`card-${card.id}`}
                checked={isSelected}
                onClick={() => selectSavedCard(card.id)}
                radioName="selectedPayment"
                title={
                  <div className="flex gap-0.5x md:gap-x">
                    <p className="text-link md:text-title_4">
                      {getCardTitle(card.card_brand)}
                    </p>
                    {card.card_last4 && (
                      <p className="text-link md:text-title_4">
                        XXXX-{card.card_last4}
                      </p>
                    )}
                  </div>
                }
                rightContent={
                  <i
                    className={`inline-block bg-cover w-[32px] h-[24px] ${card.card_brand?.toLowerCase()}-icon`}
                  />
                }
              />
            );
          })}

          <div
            key="new-card"
            className="border border-gray rounded-[8px] p-3x w-full "
          >
            <PaymentOption
              checked={!!selectedPayment?.isNew}
              onClick={selectNewCard}
              radioName="selectedPayment"
              title={
                <p className="text-link md:text-title_4">
                  {t('addPaymentCard')}
                </p>
              }
              rightContent={
                <div className="flex gap-x">
                  <div className="flex element-center">
                    <i className="inline-block bg-cover w-[32px] h-[24px] mastercard-icon" />
                  </div>
                  <div className="flex element-center">
                    <i className="inline-block bg-cover w-[32px] h-[24px] visa-icon" />
                  </div>
                </div>
              }
            />

            <div
              className={` p-0 ${
                selectedPayment?.isNew
                  ? '  border-t border-t-gray pt-3x mt-3x'
                  : ''
              }`}
              onClick={stopPropagation}
              onFocus={stopPropagation}
            >
              {selectedPayment?.isNew && (
                <div className="flex flex-col gap-3x" onClick={stopPropagation}>
                  <UserCardForm {...newCardProps} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
